<template>
  <div class="course-preview-wrap" :class="{ lms: $config.project === 'lms', 'locked': courseLocked }">
    <HeroSection :course="course" :button-disabled="disabled" />
    <HeroCard class="mobile" :course="course" />
    <div class="section">
      <div class="container">
        <div class="row">
          <div class="col col-8">
            <div v-if="course.promoVideo && (site || ($store.getters['auth/user'].simpleLms && !course.purchased))" class="video">
              <div class="video-image" @click="openVideo(course.promoVideo.id, '')">
                <img :src="getVideoThumb(course.promoVideo.thumbnail)">
              </div>
            </div>
            <div class="learn">
              <h2 class="section-title">
                <LearnIcon /> What You Learn
              </h2>
              <ul v-if="course.learn">
                <template v-for="(l, idx) in course.learn.list">
                  <li v-if="l" :key="idx">
                    <IconCheck /> {{ l }}
                  </li>
                </template>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="site || ($store.getters['auth/user'].simpleLms && !course.purchased)" class="section gray">
      <div class="container">
        <div class="row">
          <div class="col col-8">
            <div class="top-companies">
              <h2 class="h5">
                Top Choice of Leading Companies Worldwide
              </h2>
              <p>Industry leaders and professionals globally rely on this top-rated course to enhance their skills.</p>
              <div class="logos">
                <LogoOracle />
                <LogoAmazon />
                <LogoWalmart />
                <LogoAlphabet />
                <LogoMeta />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="section" :class="{ gray: !site && course.purchased }">
      <div class="container">
        <div class="row">
          <div class="col col-8">
            <h2 class="section-title">
              <OverviewIcon />Course Description
            </h2>
            <div
              class="body-2 cut-description course-description"
              :class="[overlay.courseDescription ? 'overlay light-overlay' : '', $config.project, course.purchased ? 'simple-lms' : '']"
              v-html="overview.description"
            />
            <div v-if="overview.description" class="see-more">
              <a class="link-accent" @click="toggleText($event, 'courseDescription')">
                <span>See {{ seeMore.courseDescription ? 'Less' : 'More' }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="course.status !== 'locked' && course.freeLectures && course.freeLectures.length && (site || ($store.getters['auth/user'].simpleLms && !course.purchased))"
      class="section dark"
    >
      <div class="container">
        <div class="row">
          <div class="col col-8">
            <h2 class="section-title">
              <RocketIcon />Learn for Free
            </h2>
            <div class="lectures-list row">
              <div v-for="(lecture, l) in course.freeLectures" :key="l" class="lecture col-6 col">
                <div class="wrapper">
                  <div class="lecture-image" :class="{ 'video-lesson': lecture.videoId }" @click="lecture.videoId ? openVideo(lecture.videoExtId, lecture.lectureName, lecture.thumbnail, true) : openTextLesson(lecture.lectureId, lecture.lectureName)">
                    <img v-if="lecture.videoId" :src="lecture.thumbnail" :alt="lecture.lectureName">
                    <img v-else :src="`${$config.imagesUrl}/lectures/${lecture.thumbnail || lecture.lectureImage}`" :alt="lecture.lectureName">
                  </div>
                  <p class="lecture-title body-1" @click="lecture.videoId ? openVideo(lecture.videoExtId, lecture.lectureName) : openTextLesson(lecture.lectureId, lecture.lectureName)">
                    {{ `${lecture.sectionOrder}.${lecture.lectureOrder} ${lecture.lectureName}` }}
                  </p>
                </div>
                <p v-if="lecture.videoId" class="lecture-duration caption">
                  <IconVideoLesson />{{ lecture.duration }} min
                </p>
                <p v-else class="lecture-duration caption">
                  <IconTextLesson /> {{ lecture.duration }} min
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="site || ($store.getters['auth/user'].simpleLms && !course.purchased)" class="section gray">
      <div class="container">
        <div class="row">
          <div class="col col-8">
            <div class="exercises">
              <client-only>
                <SliderInfinite align="left" :exchanged="$store.state.layout === 'mobile'">
                  <h2 slot="heading" class="section-title">
                    <InteractiveExercisesIconLarge />Interactive Exercises
                    <p slot="description" class="mobile-description">
                      Practice what you've learned with coding tasks, flashcards, fill in the blanks, multiple choice, and other fun exercises.
                    </p>
                  </h2>
                  <p slot="description" class="description">
                    Practice what you've learned with coding tasks, flashcards, fill in the blanks, multiple choice, and other fun exercises.
                  </p>
                  <div
                    v-for="(exercise, e) in exercises" slot="slide" :key="e"
                    class="exercise"
                  >
                    <img :src="require(`../../assets/images/exercises/${exercise}.webp`)" :alt="`${exercise} exercise`">
                  </div>
                </SliderInfinite>
              </client-only>
            </div>
            <button class="see-demo btn btn-primary" @click="$store.dispatch('modals/open', { name: 'video', videoId: $config.platform === 'ds' ? dsExercisesDemoId : faExercisesDemoId })">
              See a demo
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="section">
      <div class="container">
        <div class="row">
          <div class="col col-8">
            <h2 class="section-title">
              <CurriculumIcon />Curriculum
            </h2>
            <Curriculum
              class="curriculum tracking-section" :course="course" data-event="curriculum.scroll"
              @courses-gate="$store.dispatch('modals/open', {
                name: 'courses-gate', course: { ...course, info: course }
              });"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="section gray">
      <div class="container">
        <div class="row">
          <div class="col-4 col topics-box">
            <h2 class="h4">
              <TopicsIcon />Topics
            </h2>
            <div>
              <span v-for="(topic, i) in course.topics" :key="i" class="caption">{{ topic }}</span>
            </div>
          </div>
          <div class="col-4 col tags-box">
            <h2 class="h4">
              <TagsIcon />Tools & Technologies
            </h2>
            <div>
              <span v-for="(tag, id) in course.tags" :key="id">
                <components :is="require(`../../assets/svg/tech-responsive-${tag.replace(/\s/g, '-')}.svg?vue-component`)" />
                <Tooltip>
                  {{ tag }}
                </Tooltip>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="section">
      <div class="container">
        <div class="row">
          <div class="col col-8 course-requirements">
            <h2 class="section-title">
              <RequirementsIcon />Course Requirements
            </h2>
            <ul>
              <li v-for="(req, i) in course.requirements.list" :key="i">
                {{ req }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="section">
      <div class="container">
        <div class="row">
          <div class="col col-8 audience">
            <h2 class="section-title">
              <AudienceIcon />Who Should Take This Course?
            </h2>
            <p>
              Level of difficulty:
              <span class="badge" :class="difficulty.toLowerCase()">
                <components
                  :is="require(`../../assets/svg/icon-admin-difficulty-${course.difficulty}.svg?vue-component`)"
                /> {{
                  difficulty }}
              </span>
            </p>
            <ul>
              <li v-for="(audience, i) in course.audience.list" :key="i">
                {{ audience }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div v-if="site || ($store.getters['auth/user'].simpleLms && !course.purchased)" class="section gray">
      <div class="container">
        <div class="row">
          <div class="col col-8 exams-certification">
            <h2 class="section-title">
              <ExamsCertIcon />Exams and Certification
            </h2>
            <p class="description">
              A {{ $config.platformName }} Course Certificate is an excellent addition to your LinkedIn profile—demonstrating your expertise and willingness to go the extra mile to accomplish your goals.
            </p>
            <img :src="require(`../../assets/images/exams-and-certification-${$config.platform}.webp`)" alt="Exams and certification">
          </div>
        </div>
      </div>
    </div>
    <div class="section dark">
      <div class="container">
        <div class="row">
          <div class="col col-8">
            <h2 class="section-title">
              <InstructorsIcon />Meet Your Instructor
            </h2>
            <div class="instructor">
              <div class="bio">
                <div class="instructor-avatar">
                  <img
                    loading="lazy" class="instructor-image"
                    :src="`${$config.imagesUrl}/team/${course.instructor.image}`" :alt="course.instructor.name"
                  >
                </div>
                <div class="instructor-info">
                  <p class="instructor-name">
                    <a
                      v-if="$config.platform === 'ds'" class="instructor-link"
                      :href="`${$config.siteUrl}/instructors/${course.instructor.slug}/`"
                    >
                      {{ course.instructor.name }}
                    </a>
                    <span v-else class="instructor-name">{{ course.instructor.name }}</span>
                    <a
                      v-if="course.instructor.linkedin" class="linkedin-link" :href="course.instructor.linkedin"
                      target="_blank" rel="noopener"
                    >
                      <IconLinkedin />
                    </a>
                  </p>
                  <div class="caption instructor-signature">
                    <span>{{ course.instructor.signature }}</span>
                    <img
                      v-if="course.instructor.signatureCompany"
                      :src="`${$config.imagesUrl}/companies/${course.instructor.signatureCompany}`"
                      class="signature-company instructor-signature-company-logo" :alt="course.instructor.signature"
                    >
                  </div>
                  <p class="caption stat">
                    <BookIcon /> {{ course.instructor.courses }} Courses
                  </p>
                  <p class="caption stat">
                    <StarIcon /> {{ course.instructor.allCoursesReviews }} Reviews
                  </p>
                  <p class="caption stat">
                    <PeopleIcon /> {{ course.instructor.allCoursesEnrollments }} Students
                  </p>
                </div>
              </div>
              <p class="description cut-description" :class="overlay.instructorDescription ? 'overlay' : ''">
                {{ course.instructor.instructorLongDescription }}
              </p>
              <div v-if="course.instructor.instructorLongDescription" class="see-more">
                <a class="link-accent" @click="toggleText($event, 'instructorDescription')">
                  <span>See {{ seeMore.instructorDescription ? 'Less' : 'More' }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showReviews" class="section">
      <div class="container">
        <div class="row">
          <div :key="review.submitted" class="col col-8 reviews">
            <h2 class="section-title">
              <ReviewsIcon />What Our Learners Say
              <button
                v-if="course.reviews && course.reviews.count > 3"
                class="btn-link-accent"
                @click="$store.dispatch('modals/open', { name: 'course-reviews', reviews: { courseId: course.id, ...course.reviews } })"
              >
                See all reviews
              </button>
            </h2>
            <div v-if="showMyRatingSection" class="my-rating">
              <h5>My rating</h5>
              <div class="user">
                <div class="avatar">
                  <img :src="$store.getters['auth/user'].avatar" @error="$store.commit('auth/setAvatarToFallback')">
                </div>
                <p class="name">
                  {{ $store.getters['auth/user'].name }}
                </p>
              </div>
              <div class="rating">
                <div v-if="!course.reviews?.userReviews || review.updateMode" class="stars">
                  <IconReviewStar
                    v-for="i in 5" :key="i" class="icon-review-star"
                    :class="{ 'full': i <= currentStar, 'active': i === lastRated }" @mouseover="currentStar = i"
                    @mouseleave="currentStar = review.rating, lastRated = null"
                    @click="currentStar = i, review.rating = i, lastRated = i"
                  />
                </div>
                <div v-else class="stars">
                  <IconReviewStar
                    v-for="i in 5" :key="i" class="icon-review-star"
                    :class="{ 'full': i <= course.reviews.userReviews.rating }"
                  />
                </div>
                <div v-if="course.reviews?.userReviews && !review.updateMode" class="review-actions-info">
                  <small class="date">{{ course.reviews.userReviews.reviewUpdated ||
                    course.reviews.userReviews.reviewCreated }}</small>
                  <IconPencil class="edit" @click="editReview" />
                  <IconBin class="delete" @click="deleteReview" />
                </div>
              </div>
              <textarea v-if="!course.reviews?.userReviews || review.updateMode" v-model="review.comment" placeholder="Tell us more about your experience (optional)" />
              <div v-else>
                {{ course.reviews.userReviews.review }}
                <span v-if="course.reviews.userReviews.updated" class="edited-label"> (edited)</span>
              </div>
              <div v-if="!course.reviews?.userReviews || review.updateMode" class="buttons-holder">
                <button :disabled="!review.rating" class="btn-primary" @click="sendReview">
                  {{ review.updateMode ? 'Save review' : 'Leave a review' }}
                </button>
              </div>
              <hr v-if="course.reviews">
              <h5 v-if="course.reviews">
                All Reviews
              </h5>
            </div>
            <Reviews v-if="course.reviews" shortened :reviews="{ courseId: course.id, ...course.reviews }" />
          </div>
        </div>
      </div>
    </div>
    <div v-if="showProjectsSection" class="section">
      <div class="container">
        <div class="row">
          <div class="col col-8 projects">
            <Slider>
              <h2 slot="heading" class="section-title">
                <ProjectsIcon />Related Projects
              </h2>
              <p slot="description" class="description">
                Bridge the gap between theoretical knowledge and practical implementation by working on real-world {{ $config.platform === 'ds' ? 'data' : 'finance' }} projects.
              </p>
              <ProjectCard
                v-for="(p, id) in course.relatedProjects" :key="id" slot="slide"
                :project="p" heading="h6"
                class="col-6 sm-6"
              />
            </Slider>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showCareerTracksSection" class="section">
      <div class="container">
        <div class="row">
          <div class="col col-8 career-tracks">
            <Slider>
              <h2 slot="heading" class="section-title">
                <TracksIcon />Related Career Tracks
              </h2>
              <p slot="description" class="description">
                Enroll in an easy-to-follow learning path and specialize in your preferred role in {{ $config.platform === 'ds' ? 'data science' : 'finance' }}.
              </p>
              <div
                v-for="(track, t) in course.relatedCareerTracks" slot="slide" :key="t"
                class="col col-12 track"
              >
                <div class="heading">
                  <h5 class="title">
                    {{ track.trackName }}
                  </h5>
                  <nuxt-link class="btn-link-accent" :to="`/career-tracks/${track.trackSlug}/`">
                    View details
                  </nuxt-link>
                </div>
                <p class="description">
                  {{ track.trackDescription }}
                </p>
                <div class="stats">
                  <div class="courses">
                    <MonitorIcon />
                    <span class="text"> {{ track.courses }} Courses</span>
                  </div>
                  <div class="certificate">
                    <MedalIcon />
                    <span class="text">Certificate of Achievement</span>
                  </div>
                </div>
              </div>
            </Slider>
          </div>
        </div>
      </div>
    </div>
    <div v-if="site || ($store.getters['auth/user'].simpleLms && !course.purchased)" class="section gray">
      <div class="container">
        <div class="row">
          <div class="col col-8">
            <div class="features">
              <h2 class="h5">
                {{ $config.platformName }} Is Featured at
              </h2>
              <p>Our top-rated courses are trusted by business worldwide.</p>
              <div class="logos">
                <LogoForbes />
                <LogoBloobmerg />
                <LogoCnbc />
                <LogoWsj />
                <LogoUsaToday />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="section">
      <div class="container">
        <div class="row">
          <div class="col col-8">
            <div
              v-if="course.similar.length" class="similar-courses-wrapper tracking-section"
              data-event="courses_you_may_like.scroll"
            >
              <client-only>
                <SimilarCourses icon :courses="course.similar" :title="$config.project === 'lms' ? 'Related Courses' : 'Recommended Courses'" />
              </client-only>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import global from '../../mixins/global';
import Tooltip from '../../components/Tooltip';
import HeroCard from './HeroCard';
import HeroSection from './HeroSection';
import LearnIcon from '../../assets/svg/courses/what-you-learn.svg?vue-component';
import IconCheck from '../../assets/svg/icon-check.svg?vue-component';
import LogoOracle from '../../assets/svg/companies/logo-oracle.svg?vue-component';
import LogoAmazon from '../../assets/svg/companies/logo-amazon-2.svg?vue-component';
import LogoWalmart from '../../assets/svg/companies/logo-walmart.svg?vue-component';
import LogoAlphabet from '../../assets/svg/companies/logo-alphabet.svg?vue-component';
import LogoMeta from '../../assets/svg/companies/logo-meta.svg?vue-component';
import LogoForbes from '../../assets/svg/companies/logo-forbes.svg?vue-component';
import LogoBloobmerg from '../../assets/svg/companies/logo-bloomberg.svg?vue-component';
import LogoCnbc from '../../assets/svg/companies/logo-cnbc.svg?vue-component';
import LogoWsj from '../../assets/svg/companies/logo-wsj.svg?vue-component';
import LogoUsaToday from '../../assets/svg/companies/logo-usa-today.svg?vue-component';
import OverviewIcon from '../../assets/svg/courses/overview-icon.svg?vue-component';
import IconVideoLesson from '../../assets/svg/courses/icon-lesson-type.svg?vue-component';
import IconTextLesson from '../../assets/svg/courses/icon-text-lesson-type.svg?vue-component';
import RocketIcon from '../../assets/svg/courses/rocket-icon.svg?vue-component';
import InteractiveExercisesIcon from '../../assets/svg/courses/interactive-exercises.svg?vue-component';
import InteractiveExercisesIconLarge from '../../assets/svg/courses/interactive-exercises-lg.svg?vue-component';
import CurriculumIcon from '../../assets/svg/courses/curriculum-icon.svg?vue-component';
import TopicsIcon from '../../assets/svg/courses/topics-icon.svg?vue-component';
import TagsIcon from '../../assets/svg/courses/tags-icon.svg?vue-component';
import RequirementsIcon from '../../assets/svg/courses/requirements-icon.svg?vue-component';
import AudienceIcon from '../../assets/svg/courses/audience-icon.svg?vue-component';
import ExamsCertIcon from '../../assets/svg/courses/check-icon.svg?vue-component';
import InstructorsIcon from '../../assets/svg/courses/instructors-icon.svg?vue-component';
import ReviewsIcon from '../../assets/svg/courses/reviews-icon.svg?vue-component';
import Curriculum from './Curriculum';
import SimilarCourses from './SimilarCourses';
import Reviews from './Reviews';
import RoundedQuotesBig from '../../assets/svg/rounded-quotes-big.svg?vue-component';
import IconLinkedin from '../../assets/svg/icon-site-linkedin.svg?vue-component';
import BookIcon from '../../assets/svg/courses/icon-book.svg?vue-component';
import StarIcon from '../../assets/svg/courses/icon-star.svg?vue-component';
import PeopleIcon from '../../assets/svg/courses/icon-people.svg?vue-component';
import MedalIcon from '../../assets/svg/medal.svg?vue-component';
import MonitorIcon from '../../assets/svg/courses/monitor.svg?vue-component';
import ProjectsIcon from '../../assets/svg/courses/projects-icon.svg?vue-component';
import TracksIcon from '../../assets/svg/courses/tracks-icon.svg?vue-component';
import Slider from '../../components/Slider';
import SliderInfinite from '../../components/SliderInfinite';
import ProjectCard from '../../Projects/Card';
import IconReviewStar from '../../assets/svg/icon-review-star.svg?vue-component';
import IconPencil from '../../assets/svg/icon-pencil.svg?vue-component';
import IconBin from '../../assets/svg/icon-bin.svg?vue-component';

export default {
  components: {
    Tooltip,
    HeroSection,
    HeroCard,
    LearnIcon,
    IconCheck,
    LogoOracle,
    LogoAmazon,
    LogoWalmart,
    LogoAlphabet,
    LogoMeta,
    LogoForbes,
    LogoBloobmerg,
    LogoCnbc,
    LogoWsj,
    LogoUsaToday,
    OverviewIcon,
    IconVideoLesson,
    IconTextLesson,
    RocketIcon,
    InteractiveExercisesIcon,
    InteractiveExercisesIconLarge,
    CurriculumIcon,
    TopicsIcon,
    TagsIcon,
    RequirementsIcon,
    AudienceIcon,
    ExamsCertIcon,
    InstructorsIcon,
    ReviewsIcon,
    Curriculum,
    SimilarCourses,
    Reviews,
    RoundedQuotesBig,
    IconLinkedin,
    Slider,
    SliderInfinite,
    BookIcon,
    StarIcon,
    PeopleIcon,
    MonitorIcon,
    MedalIcon,
    ProjectsIcon,
    TracksIcon,
    ProjectCard,
    IconReviewStar,
    IconPencil,
    IconBin,
  },
  mixins: [global],
  props: {
    course: { type: Object, required: true },
    disabled: { type: Boolean, required: false },
  },
  data() {
    return {
      videoJSLoaded: false,
      seeMore: {
        instructorDescription: false,
        courseDescription: false,
      },
      overlay: {
        instructorDescription: false,
        courseDescription: false,
      },
      exercises: [
        'true-false', 'single-choice', 'multiple-choice', 'flashcards', 'fill-blank-typing', 'fill-blank-selection', 'order', 'categorize',
      ],
      currentStar: 0,
      lastRated: null,
      review: {
        rating: this.course?.reviews?.userReviews?.rating || 0,
        comment: this.course?.reviews?.userReviews?.review || '',
        submitted: null,
        updateMode: false,
      },
      dsExercisesDemoId: '6363332826112',
      faExercisesDemoId: '6363627543112',
    };
  },
  head() {
    const metaTitle = `${this.course.info.metaTitle}`;

    if (this.$config.project === 'site') {
      const linkedData = this.$linkedDataLoad([
        { key: 'WebSite' },
        { key: 'WebPage', values: { ...this.course.info } },
        {
          key: 'Course',
          values: {
            name: metaTitle,
            description: this.course.info.metaDescription,
            hasCourseInstance: [{
              '@type': 'CourseInstance',
              instructor: {
                '@type': 'Person',
                name: this.course.instructor.name,
              },
              courseMode: 'online',
              accessMode: 'visual',
              courseWorkload: `PT${this.course.totalHours}H`,
              educationalCredentialAwarded: `Certificate of completion - ${this.course.name}`,
              url: `${this.$config.appUrl}/courses/${this.course.slug}/`,
            }],
            offers: [{
              '@type': 'Offer',
              category: 'Paid',
              priceCurrency: 'USD',
              price: 36.00,
            }],
          },
        },
      ]);
      const link = [];

      if (this.canonicalURL) {
        link.push({ rel: 'canonical', href: this.canonicalURL() });
      }

      return {
        titleTemplate: `${metaTitle}%s`,
        link,
        meta: this.metaProps({ ...this.course.info, metaImage: this.course.bigImage || `og-${this.course.slug}${this.$store.state.campaign === 'campaign-chat-gpt' && this.course.slug === 'intro-to-chatgpt-and-generative-ai' ? '-campaign' : ''}.png` }),
        script: [
          { src: 'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js' },
          { src: 'https://players.brightcove.net/videojs-quality-menu/1/videojs-quality-menu.min.js' },
          linkedData,
        ],
      };
    }

    return { titleTemplate: `${metaTitle}%s` };
  },
  computed: {
    showReviews() {
      if (this.site) {
        return this.course.reviews && this.course.reviews.count;
      }
      return this.showMyRatingSection || (this.course.reviews && this.course.reviews.count);
    },
    showMyRatingSection() {
      return this.$config.project === 'lms' && this.course.progress === 100;
    },
    site() {
      return this.$config.project === 'site';
    },
    difficulty() {
      const options = {
        0: 'Beginner',
        1: 'Intermediate',
        2: 'Advanced',
      };
      return options[this.course.difficulty];
    },
    overview() {
      return {
        description: this.course.description,
        hours: this.course.totalHours,
        lessons: this.course.lessons,
        practices: this.course.practicalTasks,
      };
    },
    skills() {
      return {
        learn: this.course.learn,
        tags: this.course.tags,
      };
    },
    quoteInstructorSlug() {
      if (this.$route.params.course === 'data-driven-business-growth') {
        return 'tina-huang';
      }
      if (this.$route.params.course === 'learn-machine-learning-process-a-z') {
        return 'ken-jee';
      }
      if (this.$route.params.course === 'advanced-sql') {
        return 'vladimir-saev';
      }

      return this.course.instructor.slug;
    },
    quoteInstructorName() {
      if (this.$route.params.course === 'data-driven-business-growth') {
        return 'Tina Huang';
      }
      if (this.$route.params.course === 'learn-machine-learning-process-a-z') {
        return 'Ken Jee';
      }
      if (this.$route.params.course === 'advanced-sql') {
        return 'Vladimir Saev';
      }

      return this.course.instructor.name;
    },
    quoteInstructorSignature() {
      if (this.$route.params.course === 'data-driven-business-growth') {
        return '260K Subscribers on Youtube';
      }
      if (this.$route.params.course === 'learn-machine-learning-process-a-z') {
        return '220K Subscribers on Youtube';
      }
      if (this.$route.params.course === 'advanced-sql') {
        return 'Worked at PaySafe Group';
      }

      return this.course.instructor.signature;
    },
    showProjectsSection() {
      return !this.site && this.course.relatedProjects.length && !this.$store.getters['auth/user'].simpleLms;
    },
    showCareerTracksSection() {
      return !this.site && this.course.relatedCareerTracks.length && !this.$store.getters['auth/user'].simpleLms;
    },
    courseLocked() {
      return this.$config.project === 'site' ? this.course.status === 'locked' : !this.course.unlocked;
    },
  },
  async mounted() {
    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJSLoaded = true;

    window.addEventListener('scroll', this.handleScroll);

    this.$once('hook:beforeDestroy', () => {
      if (window) {
        window.removeEventListener('scroll', this.handleScroll);
      }
    });

    if (this.$config.project === 'lms') {
      setTimeout(() => {
        this.loadSeeMoreButtons();
      }, 500);
    } else {
      this.loadSeeMoreButtons();
    }
  },
  methods: {
    loadSeeMoreButtons() {
      this.$el.querySelectorAll('.cut-description').forEach((el) => {
        if (el.offsetHeight < 290) {
          if (el.nextElementSibling) {
          // eslint-disable-next-line no-param-reassign
            el.nextElementSibling.style.display = 'none';
          }
        } else {
          if (el.nextElementSibling) {
          // eslint-disable-next-line no-param-reassign
            el.nextElementSibling.style.display = 'block';
          }
          if (el.classList.contains('course-description')) {
            this.overlay.courseDescription = true;
          } else {
            this.overlay.instructorDescription = true;
          }
          // eslint-disable-next-line no-param-reassign
          el.style.height = '290px';
        }
      });
    },
    handleScroll() {
      const heroCard = document.querySelector('.hero-section.sticky .hero-card');
      const footer = document.querySelector('footer');
      if (footer && heroCard) {
        const footerPosition = footer.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;

        // Check if the footer is in view
        if (footerPosition <= windowHeight) {
          heroCard.style.opacity = '0'; // Hide the element

          // Update display to click the elements bellow
          setTimeout(() => {
            if (heroCard.style.opacity === '0') {
              heroCard.style.display = 'none';
            }
          }, 250);
        } else {
          heroCard.style.opacity = '1'; // Show the element
          heroCard.style.display = '';
        }
      }
    },
    async sendReview() {
      const reviews = await this.$axios.$post('/course/review?ncap=1', {
        data: { ...this.review, courseId: this.course.id, lectureId: 0 },
      });
      if (reviews) {
        this.$tracking.analytics().track('course_review_posted', {
          course_id: this.course.id,
          review_score: this.review.rating,
          review_text: this.review.comment,
          review_origin: 'landing',
        });

        this.review.comment = null;
        this.currentStar = 0;
        this.$set(this.course, 'reviews', reviews);
        this.review.submitted = `${new Date()}`;
        this.review.updateMode = false;
      }
    },
    async editReview() {
      this.review.updateMode = true;
      if (this.review.updateMode) {
        this.review.comment = this.course.reviews.userReviews.review;
        this.currentStar = this.course.reviews.userReviews.rating;
        setTimeout(() => {
          this.$el.querySelector('.my-rating textarea').focus();
        }, 300);
      }
    },
    async deleteReview() {
      const confirm = await this.$store.dispatch('modals/open', {
        name: 'confirm',
        title: 'Are you sure you want to delete your review?',
        confirm: 'Delete',
        confirmBtnClass: 'btn-danger',
        cancel: 'Cancel',
        cancelBtnClass: 'btn-light-shade',
      });

      if (confirm) {
        this.review.rating = 0;
        this.review.comment = '';
        const reviews = await this.$axios.$delete(`/course/user-review/${this.course.id}`);
        if (reviews) {
          this.$set(this.course, 'reviews', reviews);
          this.review.submitted = `${new Date()}`;
          this.review.updateMode = false;
        }
      }
    },
    openVideo(id, title, image, thumbnail) {
      this.$store.dispatch('modals/open', {
        name: 'lecture-preview', type: 'video', id, title, image, thumbnail, info: this.course,
      });
    },
    openTextLesson(id, title) {
      this.$store.dispatch('modals/open', {
        name: 'lecture-preview', type: 'text', id, title, info: this.course,
      });
    },
    getVideoThumb(image) {
      return `${this.$config.imagesUrl}/courses/${image}`;
    },
    toggleText(event, type) {
      if (type === 'courseDescription') {
        this.seeMore.courseDescription = !this.seeMore.courseDescription;
        this.overlay.courseDescription = !this.overlay.courseDescription;
      } else if (type === 'instructorDescription') {
        this.seeMore.instructorDescription = !this.seeMore.instructorDescription;
        this.overlay.instructorDescription = !this.overlay.instructorDescription;
      }
      const elem = event.target.closest('.link-accent').parentElement.previousElementSibling;
      if ((type === 'instructorDescription' && this.seeMore.instructorDescription)
        || (type === 'courseDescription' && this.seeMore.courseDescription)) {
        elem.style.height = `${elem.scrollHeight}px`;
        setTimeout(() => {
          elem.classList.remove('overlay');
        }, 300);
      } else {
        elem.style.height = '200px';
        setTimeout(() => {
          const to = elem.closest('.section');
          to.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    },
  },
};
</script>

<style lang="scss">
.course-preview-wrap {
  background: var(--main-light);

  .hero-card.mobile {
    display: none;
  }

  .section {
    padding-block: var(--spacing-48);

    .section-title {
      text-transform: capitalize;
      display: flex;
      align-items: center;
      font-size: 28px;
      gap: 16px;

      svg {
        *[stroke] {
          stroke: var(--accent-shade-2);
        }

        *[fill] {
          fill: var(--accent-shade-2);
        }
      }

      .btn-link-accent {
        color: var(--accent);
        font-size: 16px;
        font-weight: 600;
        padding: 0;
        margin-left: auto;
      }
    }

    .flex-container {
      display: flex;
    }

    &.gray {
      background: var(--main-light-shade-2);
    }

    &.dark {
      background: var(--main-dark);
      color: var(--main-light);
    }
  }

  .video {
    margin-bottom: var(--spacing-48);

    .video-image {
      position: relative;
      height: 433px;
      border-radius: 17px;
      overflow: hidden;
      cursor: pointer;
      width: 100%;
      text-align: center;

      img {
        height: 100%;
        max-height: 100%;
        object-fit: contain;
        object-position: center;
      }

      &:before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background-size: 100%;
        width: 94px;
        height: 94px;
        opacity: 0.5;
        border-radius: 50%;
        z-index: 1;
        background-image: url('../../assets/svg/player/play-v2.svg');
        image-rendering: -webkit-optimize-contrast;
      }

      &:hover {
        &:before {
          opacity: 1;
        }
      }
    }
  }

  .learn {
    ul {
      padding: 0;
      margin: 0;
      list-style: none;

      li {
        display: flex;
        align-items: center;
        margin-bottom: var(--spacing-16);
        line-height: 150%;

        svg {
          margin-right: var(--spacing-12);
          max-width: 14px;

          path[fill] {
            fill: var(--accent);
          }
        }
      }
    }
  }

  .features,
  .top-companies {
    .h5 {
      text-transform: capitalize;
      margin-bottom: var(--spacing-26);
    }

    .logos {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &.top-companies {
      .logos svg {
        height: 24px;

        *[fill] {
          fill: var(--main-dark-light-4);
        }
      }
    }
  }

  .topics-box {
    padding-right: var(--spacing-40);
    border-right: 1px solid var(--main-light-shade-4);

    .h4 {
      font-size: 24px;
      gap: 12px;
      display: flex;
      align-items: center;
      text-transform: capitalize;
      margin-bottom: var(--spacing-32);
    }

    div {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;

      span {
        color: var(--main-light);
        background: var(--main-dark-light-2);
        border-radius: 100px;
        padding: var(--spacing-8) var(--spacing-12);
        text-transform: capitalize;
        line-height: 110%;
      }
    }
  }

  .tags-box {
    padding-left: var(--spacing-40);

    .h4 {
      font-size: 24px;
      gap: 12px;
      display: flex;
      align-items: center;
      text-transform: capitalize;
      margin-bottom: var(--spacing-32);

      svg {
        min-width: 25px;
      }
    }

    >div {
      display: flex;
      margin-top: var(--spacing-20);

      span {
        background: var(--accent);
        border-radius: 50%;
        display: inline-flex;
        justify-content: center;
        margin-right: var(--spacing-16);
        height: 40px;
        width: 40px;
        position: relative;

        svg {
          width: 20px;
        }

        &:last-of-type {
          margin-right: 0;
        }

        &:hover {
          .tooltip {
            display: block;
            width: auto;
            border-radius: 10px;
            background: rgba(0, 12, 31, 0.8);
            padding: 12px;
            text-transform: capitalize;

            &:after {
              margin-left: -5px;
            }
          }
        }
      }
    }
  }

  .lectures-list {
    margin-top: var(--spacing-32);

    .h4 {
      margin-bottom: var(--spacing-16);
    }

    .lecture {
      min-height: 293px;
      margin-bottom: var(--spacing-24);

      .lecture-image {
        position: relative;
        height: 208px;
        border-radius: 10px;
        overflow: hidden;
        cursor: pointer;

        img {
          object-fit: cover;
          object-position: center;
          max-height: 100%;
          height: 100%;
          max-width: 100%;
          width: 100%;
        }

        &.video-lesson {
          &:before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 48px;
            height: 48px;
            opacity: 0.5;
            border-radius: 50%;
            z-index: 1;
            background-image: url('../../assets/svg/player/play-v2-sm.svg');
            image-rendering: -webkit-optimize-contrast;
          }

          &:after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(0deg, rgba(0, 12, 31, 0.3) 0%, rgba(0, 12, 31, 0.3) 100%);
          }
        }
      }

      .wrapper:hover {
        .lecture-image.video-lesson {
          outline: 2px solid var(--accent);

          &:after {
            background: linear-gradient(0deg, rgba(0, 12, 31, 0.1) 0%, rgba(0, 12, 31, 0.1) 100%);
          }
        }

        .lecture-title {
          color: var(--accent);
          text-shadow: 1px 0px 0px var(--accent);
        }
      }

      .lecture-title {
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        cursor: pointer;
        margin: var(--spacing-12) 0 var(--spacing-12);
      }

      .lecture-duration {
        margin: var(--spacing-4) 0 0;
        font-weight: 500;
        color: var(--main-light-shade-4);
        display: flex;
        align-items: center;
        gap: 4px;

        svg path[stroke] {
          stroke: var(--main-light-shade-4);
        }
      }
    }
  }

  .course-requirements {
    ul {
      padding: 0;
      margin: var(--spacing-32) 0 0;

      li {
        margin-bottom: var(--spacing-16);
        padding-left: var(--spacing-8);

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    }
  }

  .audience {
    p {
      margin-top: 0;
      margin-bottom: var(--spacing-32);
      display: inline-flex;
      gap: 4px;

      .badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-weight: 700;

        svg {
          width: 16px;
        }

        &.beginner {
          color: var(--main-green-shade-1);

          svg path[stroke],
          svg stop[stop-color] {
            stroke: var(--main-green-shade-1);
            ;
            stop-color: var(--main-green-shade-1);
          }
        }

        &.intermediate {
          color: var(--main-yellow-shade-2);

          svg path[stroke],
          svg stop[stop-color] {
            stroke: var(--main-yellow-shade-2);
            stop-color: var(--main-yellow-shade-2);
          }
        }

        &.advanced {
          color: var(--main-red);

          svg path[stroke],
          svg stop[stop-color] {
            stroke: var(--main-red);
            stop-color: var(--main-red);
          }
        }
      }
    }

    ul {
      padding: 0;
      margin: 0;

      li {
        margin-bottom: var(--spacing-16);
        padding-left: var(--spacing-8);

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    }
  }

  .exercises {
    .infinite-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .infinite-carousel .controls svg circle[stroke] {
      stroke: var(--main-dark);
    }

    .infinite-carousel .controls svg path[stroke] {
      stroke: var(--main-dark);
    }

    .infinite-carousel .carousel {
      width: calc(100% + 20px);
      padding: 10px;
      margin-left: -10px;
    }

    .h3 {
      margin: 0;

      svg {
        margin-right: var(--spacing-16);
      }
    }

    .description {
      color: var(--main-dark-light-4);
      margin: 0 0 var(--spacing-32);
    }

    .mobile-description {
      display: none;
    }

    .exercise {
      width: calc((100% - 30px) / 2);
      margin: 0;
      margin-right: var(--spacing-30);
      border-radius: 10px;
      box-shadow: 0 0 12px 0 rgba(0, 12, 31, 0.25);
      overflow: hidden;

      img {
        width: 100%;
      }
    }

    .slider-container .slider-header .controls svg circle[fill] {
      fill: transparent;
    }

    + .see-demo {
      margin-top: var(--spacing-32);
      width: 200px;
      height: 48px;
      padding: 8px 24px;
    }
  }

  .exams-certification {
    .description {
      color: var(--main-dark-light-4);
      margin: var(--spacing-32) 0;
      line-height: 150%;
    }

    img {
      display: block;
      max-width: 100%;
    }
  }

  .instructor {
    margin-top: var(--spacing-32);

    .bio {
      display: flex;
      gap: 20px;

      .instructor-avatar {
        border-radius: 20px;
        border: 1px solid var(--main-light-shade-4);
        width: 171px;
        height: 171px;
        overflow: hidden;

        img {
          object-fit: cover;
          object-position: center;
          max-width: 100%;
          max-height: 100%;
          height: 100%;
        }
      }

      .instructor-info {
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .instructor-name {
          margin: 0 0 var(--spacing-8);
          display: flex;
          align-items: center;

          .instructor-name {
            margin: 0;
          }

          .instructor-link {
            color: var(--accent);
            font-size: 21px;
            font-weight: 600;
            line-height: 110%;
          }

          .linkedin-link {
            display: flex;

            svg {
              width: 18px;
              height: 18px;
              background: #0A7ED4;
              color: var(--main-light);
              padding: 2px;
              margin-left: var(--spacing-8);
              border-radius: 2px;
            }

            &:hover {
              color: var(--main-light);

              svg {
                transform: scale(1.1);
              }
            }
          }
        }

        .instructor-signature {
          img {
            vertical-align: middle;
          }
        }

        .stat {
          display: flex;
          align-items: center;
          margin: 0;
          gap: 8px;
        }
      }
    }
  }

  .cut-description {
    margin-top: var(--spacing-24);
    line-height: 150%;
    transition: height var(--duration) ease-out;
    white-space: pre-line;

    &.overlay {
      position: relative;
      overflow: hidden;

      &:before {
        content: "";
        position: absolute;
        bottom: 0;
        background: linear-gradient(180deg, rgba(0, 12, 31, 0) 0%, #000C1F 100%);
        width: 100%;
        height: 60px;
        z-index: 1;
      }

      &.light-overlay {
        &:before {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFF 100%);
        }

        &.lms.simple-lms:before {
          background: linear-gradient(180deg, rgba(240, 240, 242, 0) 0%, #F0F0F2 100%);
        }
      }
    }

    &:not(.overlay) {
      +.see-more {
        padding-top: var(--spacing-16);

        svg {
          transform: rotate(180deg);
        }
      }
    }

    p {
      margin: 0;
    }

    ul,
    ol {
      margin: 0;
      line-height: initial;
      white-space: normal;
    }
  }

  .see-more {
    span {
      color: var(--accent-shade-1);
    }

    svg {
      vertical-align: middle;
      margin-left: var(--spacing-8);
      transition: ease all var(--duration);

      path[fill] {
        fill: var(--accent-shade-1);
      }
    }

    a {
      cursor: pointer;
    }
  }

  .features {
    p {
      color: var(--main-dark-light-4);
      line-height: 150%;
      margin: 24px 0;
    }
  }

  .my-rating {
    &:hover .rating .review-actions-info {
      .edit,
      .delete {
        display: block;
      }
    }

    h5 {
      color: var(--main-dark-light-4);
      font-weight: 600;
      margin-bottom: var(--spacing-24);
    }

    .user {
      margin-bottom: var(--spacing-30);

      .avatar {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        overflow: hidden;
        position: relative;
        display: block;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .name {
        margin-bottom: 0;
      }
    }

    .rating {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--spacing-16);

      .review-actions-info {
        display: flex;
        align-items: center;

        .date {
          color: var(--main-dark-light-4);
          font-weight: 500;
          margin-right: var(--spacing-32);
        }

        .edit {
          display: none;
          width: 24px;
          margin-right: var(--spacing-8);

          &:hover {
            cursor: pointer;
          }

          &:not(:hover) {
            path[stroke] {
              stroke: var(--main-light-shade-4);
            }
          }
        }

        .delete {
          display: none;

          path[stroke] {
            stroke: var(--main-light-shade-4);
          }

          &:hover {
            cursor: pointer;
          }

          &:hover path[stroke] {
            stroke: var(--accent);
          }
        }
      }

      .stars {
        svg {
          width: 28px;
          height: 28px;
          cursor: pointer;

          &:hover:not(.active) {
            path[fill] {
              fill: rgba(237, 200, 67, 0.5);
              stroke: var(--main-yellow);
            }
          }

          &.full:not(:hover),
          &.active {
            path[fill] {
              fill: var(--main-yellow);
            }
          }

          &:not(.active):not(.full) {
            path {
              fill: transparent;
              stroke: var(--main-yellow);
            }
          }
        }
      }
    }

    textarea {
      outline: 1px solid var(--main-light-shade-4);
      border: unset;
      border-right: 2px solid white;
      height: 120px;
      font-size: 16px;

      &::placeholder {
        font-size: 16px;
      }

      @-moz-document url-prefix() {
        scrollbar-color: var(--main-light-shade-4);
        scrollbar-width: thin;
      }

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-track {
        background: var(--main-light);
      }

      &::-webkit-scrollbar-thumb {
        background: var(--main-light-shade-4);
        border-radius: 50px;
      }

      &:focus {
        outline: 2px solid var(--accent);
      }
    }

    .edited-label {
      color: var(--main-dark-light-4);
    }

    .buttons-holder {
      margin-top: var(--spacing-16);

      > button {
        width: 100%;
        max-width: 180px;
      }
    }

    hr {
      border: 0;
      border-top: 1px solid rgba(171, 175, 181, 0.5);
      margin: var(--spacing-40) 0;
    }
  }

  .career-tracks {
    .slider-container .slider-header {
      margin: 0;
    }

    .description {
      color: var(--main-dark-light-4);
      margin: 0 0 var(--spacing-32);
    }

    .track {
      padding: var(--spacing-32);
      border-radius: 15px;
      background: var(--main-light-shade-2);

      .heading {
        display: flex;
        justify-content: space-between;
        align-items: center;

        a {
          color: var(--accent);
        }

        h5 {
          margin: 0;
        }
      }

      .description {
        margin: var(--spacing-24) 0 var(--spacing-30);
        color: var(--main-dark-light-4);
      }

      .stats {
        &,
        >* {
          display: flex;
          align-items: center;

          svg {
            margin-right: var(--spacing-8);
            width: 22px;

            path[stroke] {
              stroke: var(--main-dark);
            }
          }
        }

        .courses {
          margin-right: var(--spacing-24);
        }
      }
    }
  }

  .projects {
    .slider-container .slider-header {
      margin: 0;
    }

    .description {
      color: var(--main-dark-light-4);
      margin: 0 0 var(--spacing-32);
    }
  }

  &.locked {
    &.lms .sections .section-wrapper .lessons .lesson .lesson-name {
      color: var(--main-dark);
      font-weight: 400;
    }
  }
}

@include bp($bp-laptop) {
  .course-preview-wrap {
    .section {
      .section-title {
        font-size: 24px;
      }
    }
  }
}

@include bp($bp-mobile) {
  .course-preview-wrap {
    &.lms {
      .hero-card.mobile {
        padding-bottom: var(--spacing-48);
      }
    }

    .video .video-image {
      height: auto;
      border-radius: 10px;
      display: inline-flex;

      img {
        width: 100%;
      }

      &:before {
        width: 44px;
        height: 44px;
      }
    }

    .lectures-list .lecture {
      min-height: unset;

      .lecture-image {
        height: 183px;
        margin-bottom: var(--spacing-12);
      }
    }

    .career-tracks .slider-container .slider-header {
      justify-content: center;
    }

    .section {
      .section-title {
        flex-direction: column;
        text-align: center;
        font-size: 24px;

        .btn-link-accent {
          margin: 0 auto;
        }
      }
    }

    .topics-box {
      padding-bottom: var(--spacing-40);
      margin-bottom: var(--spacing-40);
      padding-right: var(--spacing-12);
      border-bottom: 1px solid var(--main-light-shade-4);
      border-right: 0;

      .h4 {
        flex-direction: column;
        font-size: 21px;
      }

      div {
        justify-content: center;

        span {
          text-align: center;
        }
      }
    }

    .tags-box {
      padding-left: var(--spacing-12);

      .h4 {
        flex-direction: column;
      }

      div {
        justify-content: center;
        flex-wrap: wrap;
        gap: 16px;

        span {
          margin: 0;
        }
      }
    }

    .instructor .bio {
      flex-direction: column;

      .instructor-info .stat {
        margin-bottom: var(--spacing-8);

        &:last-of-type {
          margin: 0;
        }
      }
    }

    .similar-courses-wrapper,
    .projects {
      .slider-container .slider-header {
        justify-content: center;
      }
    }

    .features,
    .top-companies {
      text-align: center;

      .logos {
        flex-wrap: wrap;
        justify-content: center;
        gap: 36px;

        svg {
          transform: scale(0.8);
        }
      }
    }

    .top-companies {
      .logos {
        gap: 16px;

        svg {
          transform: scale(1);
        }
      }
    }

    .exercises {
      text-align: center;

      .infinite-head {
        flex-direction: column;
      }

      .exercise {
        width: 100%;
      }

      .description {
        display: none;
      }

      .mobile-description {
        display: block;
        color: var(--main-dark-light-4);
        font-size: 16px;
        font-weight: 400;
        line-height: 150%;
        margin: 0;
      }

      .controls {
        margin: var(--spacing-16) 0;
      }

      .slider-container {
        .slider-header {
          justify-content: center;
        }

        .swiper {
          .exercise {
            img {
              width: 100%;
            }
          }
        }
      }

      & + .see-demo {
        width: 100%;
      }
    }
  }
}
</style>
